import { PaginationDefaults } from '@terra/shared-dto-types';
import { z } from 'zod';
import {
  FindingEntrySchema,
  MisconfigResearchRunStatusSchema,
  MisconfigCreatedSignalsEntrySchema,
  MisconfigTechnologyAssessmentSchema,
  MongoObjectIdSchema,
} from '@terra/shared-utils';

export const MisconfigResearchRunSchema = z.object({
  id: z.string(),
  technologyName: z.string(),
  versionRange: z.string(),
  technologyKey: z.string(),
  status: MisconfigResearchRunStatusSchema,
  findings: FindingEntrySchema.array().nullable(),
  findingCount: z.number().int(),
  error: z.string().nullable(),
  customPrompt: z.string().nullable().default(null),
  triggeredBy: z.string(),
  triggeredAt: z.string(),
  completedAt: z.string().nullable(),
  approvedBy: z.string().nullable(),
  approvedAt: z.string().nullable(),
  executionId: z.string().nullable(),
  createdSignals: MisconfigCreatedSignalsEntrySchema.array().default([]),
});

export const TriggerMisconfigResearchBodySchema = z.object({
  technologyName: z.string().min(1),
  versionRange: z.string().trim().optional(),
  customPrompt: z.string().trim().min(1).max(2000).optional(),
});

export const TriggerMisconfigResearchResponseSchema = z.object({
  run: MisconfigResearchRunSchema,
});

export const ListMisconfigResearchRunsQuerySchema = z.object({
  status: MisconfigResearchRunStatusSchema.optional(),
});

export const ListMisconfigResearchRunsResponseSchema = z.object({
  runs: MisconfigResearchRunSchema.array(),
});

export const GetMisconfigResearchRunParamsSchema = z.object({
  runId: MongoObjectIdSchema,
});

export const GetMisconfigResearchRunResponseSchema = z.object({
  run: MisconfigResearchRunSchema.nullable(),
});

export const ApproveMisconfigResearchParamsSchema = z.object({
  runId: MongoObjectIdSchema,
});

export const ApproveMisconfigResearchBodySchema = z.object({
  applicationId: MongoObjectIdSchema.optional(),
});

export const ApproveMisconfigResearchResponseSchema = z.object({
  run: MisconfigResearchRunSchema,
  publishedBatches: z.number().int(),
  publishedRecords: z.number().int(),
  matchedApplications: z.number().int(),
  newApplications: z.number().int(),
  updatedApplications: z.number().int(),
  skippedApplications: z.number().int(),
});

export const DeleteMisconfigResearchFindingParamsSchema = z.object({
  runId: MongoObjectIdSchema,
  findingId: z.string().min(1),
});

export const DeleteMisconfigResearchFindingResponseSchema = z.object({
  run: MisconfigResearchRunSchema,
});

export const DeleteMisconfigResearchRunParamsSchema = z.object({
  runId: MongoObjectIdSchema,
});

export const DeleteMisconfigResearchRunResponseSchema = z.object({
  runId: MongoObjectIdSchema,
  deletedAt: z.string(),
});

export type MisconfigResearchRunDto = z.infer<
  typeof MisconfigResearchRunSchema
>;
export type TriggerMisconfigResearchBody = z.infer<
  typeof TriggerMisconfigResearchBodySchema
>;
export type ListMisconfigResearchRunsQuery = z.infer<
  typeof ListMisconfigResearchRunsQuerySchema
>;
export type ApproveMisconfigResearchBody = z.infer<
  typeof ApproveMisconfigResearchBodySchema
>;
export type ApproveMisconfigResearchResponse = z.infer<
  typeof ApproveMisconfigResearchResponseSchema
>;

export const MISCONFIG_TECHNOLOGY_ASSESSMENT_LIST_MAX_SKIP = 50_000;
export const MISCONFIG_TECHNOLOGY_ASSESSMENT_LIST_MAX_LIMIT =
  PaginationDefaults.MAX_LIMIT;

export const ListMisconfigTechnologyAssessmentsQuerySchema = z.object({
  skip: z.coerce
    .number()
    .int()
    .min(0)
    .max(MISCONFIG_TECHNOLOGY_ASSESSMENT_LIST_MAX_SKIP)
    .optional()
    .prefault(0),
  limit: z.coerce
    .number()
    .int()
    .min(1)
    .max(MISCONFIG_TECHNOLOGY_ASSESSMENT_LIST_MAX_LIMIT)
    .optional()
    .prefault(PaginationDefaults.LIMIT),
});

export const ListMisconfigTechnologyAssessmentsResponseSchema = z.object({
  assessments: MisconfigTechnologyAssessmentSchema.array(),
  hasMore: z.boolean(),
  total: z.number().int().nonnegative(),
});

export const BackfillMisconfigTechnologyAssessmentsResponseSchema = z.object({
  started: z.literal(true),
});

export const ReassessMisconfigTechnologyBodySchema = z.object({
  technologyName: z.string().trim().min(1),
});

export const ReassessMisconfigTechnologyResponseSchema = z.object({
  assessment: MisconfigTechnologyAssessmentSchema,
});

export type ListMisconfigTechnologyAssessmentsQuery = z.infer<
  typeof ListMisconfigTechnologyAssessmentsQuerySchema
>;
export type ListMisconfigTechnologyAssessmentsResponse = z.infer<
  typeof ListMisconfigTechnologyAssessmentsResponseSchema
>;
export type BackfillMisconfigTechnologyAssessmentsResponse = z.infer<
  typeof BackfillMisconfigTechnologyAssessmentsResponseSchema
>;
export type ReassessMisconfigTechnologyBody = z.infer<
  typeof ReassessMisconfigTechnologyBodySchema
>;
